import type { ReactNode } from "react";

/**
 * Beiträge der Kontexte zur Startansicht einer Drückjagd (oberflaeche.md §3.1 „Ziele der Startansicht“, §4.2 … §4.4).
 * Die Startansicht gehört dem Kontext Drückjagd; Kernaktionen wie „Schuss“, „Liegeort“ oder der Suchbereich gehören den
 * Kontexten Schuss und Lage. Diese registrieren hier beim Laden ihres Moduls einen Hook, der aus der Lage der Person
 * (Rollen, Phase der Jagd) ihre Kernaktionen und Bereiche liefert. Die Startansicht setzt daraus die Hauptaktion (an
 * erster Stelle in `main`, nach der Zeile der Rolle in §4.3), die übrigen Kernaktionen (direkt in `navigation`
 * „Drückjagd“, nie nur über „mehr“) und die Bereiche (z. B. Suchbereiche, in `main`) zusammen.
 */
export type Phase = "vor Beginn" | "während" | "danach";

export interface Startlage {
  drueckjagdId: string;
  uid: string | null;
  /** neutrale Rollen der Person in dieser Drückjagd (Meine Drückjagden) */
  rollen: readonly string[];
  /** Revier, in dessen Einteilung die Person mitwirkt (Lesemodelle eines Reviers in der Drückjagd) */
  revier: string | null;
  phase: Phase;
}

export interface Kernaktion {
  begriff: string;
  symbol: string;
  /** Ziel innerhalb der App */
  pfad: string;
  /** Zeile der Rolle in oberflaeche.md §4.3 (0 = Schütze … 9 = Wildversorgung): die kleinste ist die Hauptaktion */
  rang: number;
  /** ist in dieser Phase die Hauptaktion der Rolle (bzw. ihr Rückfall, `rueckfall`) */
  haupt: boolean;
  /**
   * Meldung mit einem Fingertipp ohne Ablauf (§5: „Stand erreicht“, „am Start“, „am Sammelpunkt“): Statt zu `pfad` zu
   * wechseln, erfasst das Antippen die Meldung selbst (Bestätigung und Rückgängig-Leiste stehen auf der Startansicht).
   */
  ausloesen?: () => void;
  /**
   * Hauptaktion ohne Kernaktion nach N-09 (z. B. „Lagebild“, „Abholliste“, „Wildsichtung“): Ist sie nicht die Hauptaktion
   * der Startansicht, zählt sie zu den weiteren Zielen (höchstens vier direkt, die übrigen in „mehr“, §3.1 V-12).
   */
  weiteresZiel?: boolean;
  /**
   * Hauptaktion nur, wenn keine Zeile der Person in dieser Phase eine andere hat (z. B. „Schuss“ vor Beginn, sobald der
   * Stand erreicht ist; „Hunde im Treiben“, solange die Nachsuchenführerin keine Suche hat). Sie zählt erst, wenn alle
   * Beiträge `bereit` sind, damit die Hauptaktion nicht wechselt, sobald die eigentliche nachgeladen ist.
   */
  rueckfall?: boolean;
  /** steht nur als Hauptaktion; sonst führt die Startansicht das Ziel bei den Zielen der Rolle (drueckjagd/ziele.ts) */
  nurAlsHaupt?: boolean;
}

/** Weiteres Ziel eines Kontexts (§4.3 Sekundärziele, z. B. „Hund gesichtet“, solange eine Vermisstmeldung offen ist). */
export interface Startziel {
  begriff: string;
  symbol: string;
  pfad: string;
  /** steht vor den Zielen der Rolle (dringend, z. B. ein vermisster Hund), sonst danach */
  vorn?: boolean;
}

/**
 * Bestätigungsdialog eines Kontexts an Stelle der Startansicht (design/README §9.5, Referenz `#s-dialog-nachsuche`): Die
 * Startansicht zeigt dann Zustandsband und `main` mit diesem Namen und Titel; `inhalt` ist der `alertdialog`.
 */
export interface Startdialog {
  name: string;
  titel: string;
  inhalt: ReactNode;
}

export interface Startbeitrag {
  kernaktionen: readonly Kernaktion[];
  /** geöffneter Bestätigungsdialog (z. B. Nachsuchenergebnis, §5) */
  dialog?: Startdialog;
  /** Bereiche in `main` nach der Hauptaktion (z. B. `region` „Nachsuche Stand 7a“, §4.4) */
  bereiche?: ReactNode;
  /** weitere Ziele: höchstens vier aller weiteren Ziele stehen direkt, die übrigen in „mehr“ (§3.1 V-12) */
  weitere?: readonly Startziel[];
  /** alle Lesemodelle gelesen, von denen die Hauptaktionen des Beitrags abhängen (fehlt: bereit) */
  bereit?: boolean;
}

/** Ein React-Hook; die Liste steht fest, sobald die Module geladen sind (Reihenfolge der Hooks bleibt gleich). */
export type Beitragshook = (lage: Startlage) => Startbeitrag;

const beitraege: Beitragshook[] = [];

export function startbeitragRegistrieren(hook: Beitragshook): void {
  if (!beitraege.includes(hook)) beitraege.push(hook);
}

export function useStartbeitraege(lage: Startlage): Startbeitrag[] {
  return beitraege.map((hook) => hook(lage));
}

/**
 * Phase der Jagd für die Hauptaktion (oberflaeche.md §4.3): „während“ = Jagd im Gange, „vor Beginn“ = vor dem ersten
 * Jagdabschnitt ohne ausgerufenes Hahn in Ruh, sonst „danach“.
 */
export function phaseZu(jetzt: Date, imGange: boolean, beginne: readonly Date[], hahnInRuhAusgerufen: boolean): Phase {
  if (imGange) return "während";
  if (hahnInRuhAusgerufen || beginne.some((b) => b.getTime() <= jetzt.getTime())) return "danach";
  return "vor Beginn";
}
